import { z } from "zod";

import { ctaButtonSchema } from "../extensions/cta/ctaSchema";

export const MAX_MEDIA_ATTACHMENTS = 1;
export const MMS_BODY_CHARACTER_LIMIT = 1600;
export const MAX_MEDIA_FILE_SIZE_BYTES = 500 * 1024;

export const ALLOWED_MEDIA_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  // Contact cards (vCard) attach as an MMS DOCUMENT, not an image.
  "text/vcard",
] as const;

// Infobip Messages API body type for a media attachment: images send as
// IMAGE, everything else (vCards) as DOCUMENT.
export function mediaBodyType(mimeType: string): "IMAGE" | "DOCUMENT" {
  return mimeType.startsWith("image/") ? "IMAGE" : "DOCUMENT";
}

// A non-image attachment (currently only vCard contact cards) renders as a
// contact chip/pill rather than a thumbnail.
export function isDocumentMedia(mimeType: string): boolean {
  return mediaBodyType(mimeType) === "DOCUMENT";
}

// Display name for a contact-card chip, derived from the filename (we don't
// parse the vCard). Falls back to a generic label.
export function vcardDisplayName(name: string): string {
  return name.replace(/\.vcf$/i, "").trim() || "Contact card";
}

const mediaMimeTypeSchema = z.enum(ALLOWED_MEDIA_MIME_TYPES);

export const mediaSchema = z
  .object({
    name: z.string().min(1),
    mimeType: mediaMimeTypeSchema,
    sizeBytes: z.number().int().nonnegative().max(MAX_MEDIA_FILE_SIZE_BYTES),
    cdnUrl: z.string().min(1),
  })
  .strict();

export const mmsAuthoringSchema = z
  .object({
    bodyText: z.string().max(MMS_BODY_CHARACTER_LIMIT),
    media: z.array(mediaSchema).length(MAX_MEDIA_ATTACHMENTS),
    ctaButton: ctaButtonSchema.optional(),
  })
  .strict();

export function isAllowedMediaMimeType(
  value: string,
): value is (typeof ALLOWED_MEDIA_MIME_TYPES)[number] {
  return ALLOWED_MEDIA_MIME_TYPES.includes(
    value as (typeof ALLOWED_MEDIA_MIME_TYPES)[number],
  );
}
